<template>
  <div class="container-login text-left p-4">
    <h4 v-show="show !== 'initial'" class="text-primary fw-bold" style="cursor: pointer" @click="handleBackClick()">
      <i class="fa-solid fa-chevron-left me-2 d-inline"></i>
      {{ formTitle }}
    </h4>
    <template v-if="show === 'initial'">
      <h4 class="text-primary title pb-3">{{ $t('home.form.personalInformation.iniciarSesionRegistrarReservar') }}</h4>

      <span class="title-field text-primary mb-2">
        {{ $t('home.form.personalInformation.emailLogin') }}
      </span>
      <form autocomplete="on" @submit.prevent="validateEmail(informacionLogin.email)">
        <input
          id="email"
          v-model="informacionLogin.email"
          type="email"
          class="form-control"
          :placeholder="$t('home.form.personalInformation.email')"
          autocomplete="email"
          name="email"
          required />
        <div class="my-3 text-center">
          <button type="submit" class="btn btn-primary w-100">
            {{ $t('botones.continuar') }}
          </button>
        </div>
      </form>

      <div v-if="!isBodoSound && !isMandarinaSports && isGoogleOauthEnabled" class="divider">
        <span class="divider-text">o</span>
      </div>
      <div v-if="!isBodoSound && !isMandarinaSports && isGoogleOauthEnabled" class="text-center mt-1">
        <button
          class="btn btn-primary w-100 google-btn d-flex justify-content-center align-items-center m-auto"
          @click="doGoogleOauthLogin">
          <img src="@/assets/img/logo_google.svg" alt="Logo de Google" class="me-3" />
          <span class="text-primary fw-semibold text-mobile">
              {{ $t('header.login.continuarGoogle') }}
            </span>
        </button>
      </div>
    </template>
    <template v-else>
      <LoginForm
        :email="informacionLogin.email"
        :show-register="show === 'register'"
        @mostrar-login="mostrarLogin"
        @mostrar-recuperar-password="mostrarRecuperarPassword"
        @usuario-logueado="usuarioLogueadoHandler" />
    </template>
  </div>
</template>

<script setup>
  import { alertAutoCloseSuccessWithoutCallback } from '@/services/utils/AlertUtils';
  import { computed, reactive, ref } from 'vue';
  import UsuarioApiService from '../../services/api/skiandnight/UsuarioApiService';
  import { controlErrorRegistrarLoginUsuario } from '@/services/utils/ErrorControl';
  import LoginForm from '@/components/form/LoginForm.vue';
  import { useI18n } from 'vue-i18n';
  import { googleSdkLoaded } from 'vue3-google-login';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { useUserStore } from '@/store_pinia/userStore';
  import { agenciaStoreModule } from '@/store_pinia/agencia';

  const emit = defineEmits(['logueado', 'recordar']);

  const userStore = useUserStore();
  const loadingStore = useLoadingStore();
  const agenciaStore = agenciaStoreModule();
  const { t } = useI18n();

  const informacionLogin = reactive({
    email: null,
    password: null,
  });

  const show = ref('initial');
  const isBodoSound = ref(window.location.hostname.includes('bodosound'));
  const isMandarinaSports = ref(window.location.hostname.includes('mandarinasports'));
  const formTitle = ref(t('header.login.welcome'));

  const isGoogleOauthEnabled = computed(() => agenciaStore.getGoogleOauth);

  async function validateEmail(email) {
    try {
      loadingStore.show();
      const response = await UsuarioApiService.validateEmail(email);
      show.value = response ? 'login' : 'register';
      setFormTitle(response ? 'login' : 'register');
    } catch (error) {
      console.error(error);
      show.value = 'register';
    } finally {
      loadingStore.hide();
    }
  }

  function usuarioLogueadoHandler() {
    emit('logueado');
  }

  function handleBackClick() {
    show.value = 'initial';
    setFormTitle();
  }

  function mostrarLogin() {
    setFormTitle('homeLogin');
    show.value = 'initial';
  }

  function mostrarRecuperarPassword(email) {
    emit('recordar', email);
  }

  function setFormTitle(formType) {
    switch (formType) {
      case 'homeLogin':
        formTitle.value = t('header.login.welcome');
        break;
      case 'login':
        formTitle.value = t('home.form.personalInformation.welcomeBack');
        break;
      case 'register':
        formTitle.value = t('header.login.tusDatos');
        break;
      case 'resetPassword':
        formTitle.value = t('header.login.restablecerPassword');
        break;
      default:
        formTitle.value = t('header.login.welcome');
        break;
    }
  }

  function doGoogleOauthLogin() {
    googleSdkLoaded(google => {
      google.accounts.oauth2
        .initCodeClient({
          client_id: agenciaStore.getGoogleOauth,
          scope:
            'email profile openid https://www.googleapis.com/auth/user.birthday.read https://www.googleapis.com/auth/user.phonenumbers.read',
          callback: response => regAndLoginGoogle(response),
        })
        .requestCode();
    });
  }

  async function regAndLoginGoogle(callbackResponse) {
    try {
      loadingStore.show();
      const response = await UsuarioApiService.registrarYLoguearGoogle(callbackResponse);
      userStore.login(response.token);
      alertAutoCloseSuccessWithoutCallback(2000);
      emit('logueado');
    } catch (error) {
      console.error(error);
      controlErrorRegistrarLoginUsuario(error);
    } finally {
      loadingStore.hide();
    }
  }
</script>

<style lang="scss" scoped>
  .container-login {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(33, 36, 68, 0.25);

    .title {
      font-size: 20px;
      font-weight: 500;
      letter-spacing: 0.309px;
    }

    .title-field {
      font-size: 14px;
      font-weight: 400;
    }

    .divider {
      text-align: center;
      margin: 10px 0;
      position: relative;
    }

    .divider-text {
      background-color: white;
      padding: 0 10px;
      position: relative;
      z-index: 1;
    }

    .divider::after {
      content: '';
      background: rgba(33, 36, 68, 0.3);
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      z-index: 0;
    }
  }

  .login {
    max-width: 550px;
    margin: auto;
  }

  .register {
    max-width: 850px;
    margin: auto;
  }

  .cambio-vista {
    cursor: pointer;
    transition: 0.3s ease-in-out;
    color: white;

    &:hover {
      color: var(--bs-secondary);
    }
  }

  .cambio-vista-2 {
    cursor: pointer;
    transition: 0.3s ease-in-out;

    &:hover {
      color: var(--bs-secondary);
    }
  }

  .form-check-input:checked[type='checkbox'],
  .form-check-input[type='checkbox'] {
    border-radius: 0;
  }

  .google-btn {
    border: 1px solid var(--bs-primary) !important;
    box-shadow: none !important;
    background-color: transparent !important;
    transition: all 0.3s ease;
    @media only screen and (max-width: 768px) {
      font-size: 14px !important;
    }
  }

  .google-btn:hover {
    background-color: var(--bs-primary) !important;

    span {
      transition: all 0.5s ease;
      color: #ffffff !important;
    }
  }
</style>
